import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useMessaging } from "@/context/MessagingContext";
import { ConversationList } from "@/components/messaging/ConversationList";
import { ChatThread } from "@/components/messaging/ChatThread";
import { NewConversationDialog } from "@/components/messaging/NewConversationDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, MessageSquare, Mail } from "lucide-react";

function PresencePanel() {
  const [data, setData] = useState({ members: [], invited: [] });
  useEffect(() => {
    const load = () => api.get("/presence").then(({ data }) => setData(data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  const online = data.members.filter((m) => m.online);
  return (
    <Card className="p-4" data-testid="presence-panel">
      <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground">Online now ({online.length})</p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {data.members.map((m) => (
              <span key={m.user_id} data-testid={`presence-${m.user_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs">
                <span className={`h-2 w-2 rounded-full ${m.online ? "bg-emerald-500" : "bg-slate-400"}`} /> {m.name}
              </span>
            ))}
          </div>
        </div>
        {data.invited.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground">Invited ({data.invited.length})</p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {data.invited.map((i) => (
                <span key={i.email} data-testid={`invited-${i.email}`} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-amber-500/50 px-2.5 py-1 text-xs text-amber-600 dark:text-amber-400">
                  <Mail className="h-3 w-3" /> {i.email} · {i.role}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function Messages() {
  const { conversations, activeId, openConversation, closeConversation } = useMessaging();
  const [showNew, setShowNew] = useState(false);

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Messages</h1>
          <p className="mt-1 text-sm text-muted-foreground">Chat with your team and partner agencies in real time.</p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="new-conversation-button"><Plus className="mr-2 h-4 w-4" /> <span className="hidden sm:inline">New message</span><span className="sm:hidden">New</span></Button>
      </div>

      <PresencePanel />

      <Card className="grid h-[calc(100vh-14rem)] grid-cols-1 overflow-hidden md:grid-cols-[320px_1fr]">
        {/* List: hidden on mobile once a conversation is open */}
        <div className={`overflow-y-auto border-r border-border ${activeId ? "hidden md:block" : "block"}`}>
          <ConversationList conversations={conversations} activeId={activeId} onSelect={openConversation} />
        </div>
        {/* Thread: full-width on mobile when active, with a back button */}
        <div className={`${activeId ? "block" : "hidden md:block"}`}>
          {activeId ? <ChatThread onBack={closeConversation} /> : (
            <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
              <MessageSquare className="h-10 w-10 opacity-40" />
              <p className="mt-3 text-sm">Select a conversation to start chatting</p>
            </div>
          )}
        </div>
      </Card>

      <NewConversationDialog open={showNew} onClose={() => setShowNew(false)} />
    </div>
  );
}
